// Shared ball-game state hook — the useReducer engine behind both Ball Sort
// and Sortaballs. Each game binds its own defaults (initial board, capacity)
// via a thin wrapper; the reducer logic here is identical for both.

import { useCallback, useEffect, useReducer, useRef } from "react"

import type { CommittedMove, GameState, Move } from "../sortaballs-8-colors/logic"
import type { MovingBall } from "./types"

import { logger } from "../modules/logger/logger"
import { rangeMap } from "../modules/utils/range-map"
import { applyMoveGroup, canMoveGroup, topRunLength } from "../sortaballs-8-colors/logic"

// A move intent the player committed while a ball was in flight. Accepted and
// queued (FIFO); each is replayed on the board when the previous flight lands,
// so the player can stack moves ahead of the single animation slot.
type QueuedMove = CommittedMove

type Action =
  | {
      demo?: boolean
      index: number
      selectAll?: boolean
      // While a flight is airborne a move can never commit; taps still run
      // normal selection (select / grow / deselect / switch).
      noMove?: boolean
      type: "SELECT_PLATE"
    }
  | { count: number; from: number; to: number; type: "QUEUE_MOVE" }
  | { type: "APPLY_QUEUED_MOVE" }
  | { type: "CLEAR_MOVING_BALL" }
  | { type: "CLEAR_SELECTION" }
  | { type: "RESET" }
  | { type: "RESEED" }
  | { type: "UNDO" }

interface State {
  board: GameState
  // Increments on every flight start, so the FlyingBall overlay remounts per
  // flight and always departs from its own source slot.
  flightKey: number
  history: GameState[]
  moveCount: number
  // Moves actually committed in order — the persistence source, so a restored
  // session can replay them and rebuild this same state.
  moves: CommittedMove[]
  // The group currently flying (null when idle). One flight per move, so a new
  // move cannot start until this clears.
  movingBall: MovingBall | null
  // Moves committed mid-flight, waiting for their flight slot (FIFO).
  pendingQueue: QueuedMove[]
  selectedPlate: null | number
  // Number of same-color balls selected on the selected plate (1 = single).
  selectionCount: number
}

export const useBallGame = (
  initialBoard: GameState,
  capacity: number | readonly number[],
  initialMoves: CommittedMove[] = [],
)=> {
  // A pristine game from the current initial board — the target of a user
  // reset (and of help-reveal replays that reset first).
  const freshState = (): State=> ({
    board: initialBoard,
    flightKey: 0,
    history: [],
    moveCount: 0,
    moves: [],
    movingBall: null,
    pendingQueue: [],
    selectedPlate: null,
    selectionCount: 0,
  })

  // Commits one replayed move, mirroring the reducer's commit branch: move the
  // board, push the previous board onto history, count the move. Re-validated
  // against the board it replays onto, so corrupt stored data throws instead
  // of silently producing a wrong board — the seed falls back to a fresh
  // board and reports (see createInitialState).
  const applyCommittedMove = (state: State, move: CommittedMove): State=> {
    if (
      !canMoveGroup({
        capacity,
        count: move.count,
        move: { from: move.from, to: move.to },
        state: state.board,
      })
    ) {
      throw new Error(
        `Cannot replay restored move ${move.from}->${move.to} ×${move.count}`,
      )
    }
    return {
      ...state,
      board: applyMoveGroup(state.board, { from: move.from, to: move.to }, move.count),
      history: [...state.history, state.board],
      moveCount: state.moveCount + 1,
      moves: [...state.moves, move],
    }
  }

  // Mounted seed: replay any restored moves so a reloaded session regains the
  // exact board, history and move count it had when it was saved.
  const createInitialState = (): State=> {
    const fresh = freshState()
    if (initialMoves.length === 0) return fresh
    try {
      return initialMoves.reduce(applyCommittedMove, fresh)
    } catch (error) {
      // The stored move list is boundary data; a corrupt payload falls back to
      // a fresh board rather than crashing the game on mount — but the
      // corruption is reported (console + Sentry + toast), never swallowed.
      logger({ input: error as Error, level: "error" })
      return fresh
    }
  }

  // One flight for the whole selected group. Slots are absolute (0 = bottom of
  // the plate), computed against the pre-move board so each ball's geometry
  // stays consistent: ball i leaves source slot srcLen - 1 - i and lands at
  // dest slot dstLen + (count - 1 - i). That keeps the source order — the top
  // ball of the source arrives at the top of the moved group instead of being
  // flipped to the bottom — which also matches the committed board (the moved
  // run is a single color, so the two balls fly without crossing). All balls
  // fly together in a single flight.
  interface MovingGroupInput {
    board: GameState
    count: number
    from: number
    to: number
  }

  const buildMovingGroup = (input: MovingGroupInput): MovingBall | null=> {
    const { board, count, from, to } = input
    const source = board[from]
    if (!source) return null
    const srcLen = source.length
    const dstLen = board[to]?.length ?? 0
    return {
      balls: rangeMap(count, (i)=> ({
        // SAFETY: source array elements are color strings
        color: source[srcLen - 1 - i] as string,
        fromSlot: srcLen - 1 - i,
        toSlot: dstLen + (count - 1 - i),
      })),
      count,
      from,
      to,
    }
  }

  const reducer = (state: State, action: Action): State=> {
    switch (action.type) {
      case "CLEAR_MOVING_BALL":
        // The single flight finished — drop the overlay. The board was already
        // updated at commit time, so nothing else to mutate.
        return { ...state, movingBall: null }

      case "CLEAR_SELECTION":
        return { ...state, selectedPlate: null, selectionCount: 0 }

      case "QUEUE_MOVE": {
        // The move was already validated against the logical board by the
        // click wrapper; it replays once its flight slot is free.
        return {
          ...state,
          pendingQueue: [
            ...state.pendingQueue,
            { count: action.count, from: action.from, to: action.to },
          ],
        }
      }

      case "APPLY_QUEUED_MOVE": {
        // Replay the oldest queued move on the rendered board: pop it, apply
        // it, and arm its flight. No re-validation — validity was decided
        // against the logical board when the move was queued.
        const queued = state.pendingQueue[0]
        if (!queued) return state
        const group = buildMovingGroup({
          board: state.board,
          count: queued.count,
          from: queued.from,
          to: queued.to,
        })
        if (!group) return state
        return {
          board: applyMoveGroup(
            state.board,
            { from: queued.from, to: queued.to },
            queued.count,
          ),
          flightKey: state.flightKey + 1,
          history: [...state.history, state.board],
          moveCount: state.moveCount + 1,
          moves: [...state.moves, { count: queued.count, from: queued.from, to: queued.to }],
          movingBall: group,
          pendingQueue: state.pendingQueue.slice(1),
          selectedPlate: null,
          selectionCount: 0,
        }
      }

      case "RESET":
        // A user reset (or a help-reveal that resets first) returns to the
        // ORIGINAL initial board — never re-replays restored moves.
        return freshState()

      case "RESEED":
        // The mounted puzzle changed (navigation): rebuild the state from the
        // current initial board, replaying that puzzle's restored moves.
        return createInitialState()

      case "SELECT_PLATE": {
        const { index, selectAll } = action
        const { board, selectedPlate } = state

        // Long press (mobile) selects the plate's full top run in one gesture —
        // no repeated taps to grow the selection.
        if (selectAll) {
          const plate = board[index]
          if (!plate) return state
          const run = topRunLength(plate)
          if (run === 0) return state
          return { ...state, selectedPlate: index, selectionCount: run }
        }

        // No plate selected yet → select this one (only if non-empty).
        if (selectedPlate === null) {
          if (board[index]?.length === 0) return state
          return { ...state, selectedPlate: index, selectionCount: 1 }
        }

        // Clicked same plate → grow the selection while the next ball down
        // shares the top color; when it can't, deselect entirely.
        if (selectedPlate === index) {
          const plate = board[index]
          if (!plate) return state
          const nextCount = state.selectionCount + 1
          const topColor = plate[plate.length - 1]
          const nextBallColor = plate[plate.length - nextCount]
          if (nextBallColor !== undefined && nextBallColor === topColor) {
            return { ...state, selectionCount: nextCount }
          }
          return { ...state, selectedPlate: null, selectionCount: 0 }
        }

        // Clicked a different plate → attempt a group move. While a flight is
        // airborne (noMove) the move is never allowed, so it falls through to
        // the same switch-selection fallback as an illegal move: make the
        // tapped plate active (or clear the selection on an empty plate), so a
        // pick can always be dropped by tapping elsewhere.
        const count = state.selectionCount
        const move: Move = { from: selectedPlate, to: index }
        if (action.noMove || !canMoveGroup({ capacity, count, move, state: board })) {
          // The destination can't take the selected group (or a flight is
          // airborne). Never swallow the tap: discard the current selection
          // and make the clicked plate active (or clear the selection on an
          // empty plate), so a multi-ball pick can always be dropped by
          // tapping elsewhere.
          if (board[index]?.length === 0) {
            return { ...state, selectedPlate: null, selectionCount: 0 }
          }
          return { ...state, selectedPlate: index, selectionCount: 1 }
        }

        const group = buildMovingGroup({
          board,
          count,
          from: selectedPlate,
          to: index,
        })
        if (!group) return state

        return {
          board: applyMoveGroup(board, move, count),
          flightKey: state.flightKey + 1,
          history: action.demo ? state.history : [...state.history, board],
          moveCount: action.demo ? state.moveCount : state.moveCount + 1,
          moves: action.demo
            ? state.moves
            : [...state.moves, { count, from: move.from, to: move.to }],
          movingBall: group,
          pendingQueue: state.pendingQueue,
          selectedPlate: null,
          selectionCount: 0,
        }
      }

      case "UNDO": {
        if (state.history.length === 0) return state
        const prev = state.history[state.history.length - 1]
        return {
          // SAFETY: history entries are GameState snapshots
          board: prev as GameState,
          flightKey: 0,
          history: state.history.slice(0, -1),
          moveCount: state.moveCount - 1,
          moves: state.moves.slice(0, -1),
          movingBall: null,
          pendingQueue: [],
          selectedPlate: null,
          selectionCount: 0,
        }
      }

      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, createInitialState())

  // Fresh snapshot of `state` so the click wrappers read current values without
  // re-subscribing on every render.
  const stateRef = useRef(state)
  stateRef.current = state

  // Reseed when the board changes (e.g. navigating to another puzzle): rebuild
  // the state from the new initial board, replaying that puzzle's restored
  // moves. This must happen during render, not in an effect: an effect would
  // run after the commit with the stale solved state, letting callers' effects
  // save progress for the new puzzle id while the old board still looks solved.
  const prevInitialBoard = useRef(initialBoard)
  if (prevInitialBoard.current !== initialBoard) {
    prevInitialBoard.current = initialBoard
    dispatch({ type: "RESEED" })
  }

  // Logical board = the rendered board plus every queued-but-not-yet-flown
  // move. Stacked moves compose against it, so a commit is validated here,
  // never against the lagging rendered board. Computed fresh from the current
  // state — no ref to drift out of sync with idle commits or the drain.
  const intendedBoard = (st: State): GameState =>
    st.pendingQueue.reduce(
      (board, queued)=> applyMoveGroup(board, { from: queued.from, to: queued.to }, queued.count),
      st.board,
    )

  // Click wrapper: while a flight is airborne (or moves wait in the queue) a
  // commit is accepted and queued against the logical board instead of being
  // rejected; every other tap behaves exactly as on a static board (tap / grow
  // / long-press). The one special case: tapping the DESTINATION of the
  // current flight while already holding another selection is a no-op — a move
  // into the airborne destination can never commit, and switching to it would
  // silently discard the selection the player just made.
  const selectPlate = useCallback(
    (index: number)=> {
      const st = stateRef.current
      if (
        st.movingBall !== null &&
        index === st.movingBall.to &&
        st.selectedPlate !== null &&
        st.selectedPlate !== index
      ) {
        return
      }
      const animating = st.movingBall !== null || st.pendingQueue.length > 0
      if (!animating) {
        dispatch({ index, type: "SELECT_PLATE" })
        return
      }
      // Selection-only taps follow the rendered board — no move intent.
      const tappedEmpty = (st.board[index]?.length ?? 0) === 0
      if (
        st.selectedPlate === null ||
        index === st.selectedPlate ||
        tappedEmpty
      ) {
        dispatch({
          index,
          noMove: tappedEmpty,
          type: "SELECT_PLATE",
        })
        return
      }
      // A commit while animating: validate against the logical board, and when
      // legal, base the queued move on it — never on the lagging board.
      const move: Move = { from: st.selectedPlate, to: index }
      const count = st.selectionCount
      if (canMoveGroup({ capacity, count, move, state: intendedBoard(st) })) {
        dispatch({ count, from: move.from, to: move.to, type: "QUEUE_MOVE" })
        dispatch({ type: "CLEAR_SELECTION" })
        return
      }
      // Illegal on the logical board → the tap keeps its static-board meaning:
      // switch the selection (or clear it on an empty plate).
      dispatch({ index, noMove: true, type: "SELECT_PLATE" })
    },
    [capacity, dispatch],
  )

  const selectAll = useCallback(
    (index: number)=> {
      const st = stateRef.current
      if (
        st.movingBall !== null &&
        index === st.movingBall.to &&
        st.selectedPlate !== null &&
        st.selectedPlate !== index
      ) {
        return
      }
      dispatch({ index, selectAll: true, type: "SELECT_PLATE" })
    },
    [dispatch],
  )

  const reset = useCallback(()=> dispatch({ type: "RESET" }), [dispatch])

  // Don't let undo run mid-flight or mid-queue: it would corrupt the in-flight
  // state or the stacked moves.
  const undo = useCallback(()=> {
    if (
      stateRef.current.movingBall !== null ||
      stateRef.current.pendingQueue.length > 0
    ) {
      return
    }
    dispatch({ type: "UNDO" })
  }, [dispatch])

  const animatingFlag = state.movingBall !== null

  // Drain the queue: as soon as a flight finishes and moves wait, replay the
  // next one through the normal animation slot. Moves replay one per flight
  // (FIFO), so the single-flight constraint holds end to end.
  useEffect(()=> {
    if (state.movingBall !== null || state.pendingQueue.length === 0) return
    dispatch({ type: "APPLY_QUEUED_MOVE" })
  }, [state.movingBall, state.pendingQueue])

  return {
    board: state.board,
    clearMovingBall: useCallback(()=> dispatch({ type: "CLEAR_MOVING_BALL" }), [dispatch]),
    clearSelection: useCallback(()=> dispatch({ type: "CLEAR_SELECTION" }), [dispatch]),
    // Committing counterpart of demoSelectPlate: applies the selection exactly
    // like a player click (history, moveCount, moves) — used by the help reveal
    // so the moves the player is shown are undoable and persist.
    commitSelectPlate: useCallback(
      (index: number)=> dispatch({ index, type: "SELECT_PLATE" }),
      [dispatch],
    ),
    demoSelectPlate: useCallback(
      (index: number)=> dispatch({ demo: true, index, type: "SELECT_PLATE" }),
      [dispatch],
    ),
    flightKey: state.flightKey,
    groupSize: state.movingBall?.count ?? 1,
    isAnimating: animatingFlag,
    moveCount: state.moveCount,
    moves: state.moves,
    movingBall: state.movingBall,
    reset,
    selectAll,
    selectedPlate: state.selectedPlate,
    selectionCount: state.selectionCount,
    selectPlate,
    undo,
  }
}

export type { MovingBall }